import React from 'react';
import { useMutation } from 'react-query';
import { Button } from '@raptormaps/button';
import { Text } from '@raptormaps/text';
import { Row } from '@raptormaps/layout';
import { Checkbox } from '@raptormaps/checkbox';
import {
  getOffboardUserMutation,
  UserSearchResult,
} from '../../api/services/users';

export type RowState = 'idle' | 'confirming' | 'offboarding' | 'offboarded';

export type OffboardResult = {
  orgs_removed: { id: number; name: string }[];
};

export interface UserRowProps {
  user: UserSearchResult;
  rowState: RowState;
  checked: boolean;
  offboardResult: OffboardResult | null;
  onSetState: (state: RowState) => void;
  onSetChecked: (checked: boolean) => void;
  onOffboardResult: (result: OffboardResult) => void;
  onError: () => void;
}

export function UserRow({
  user,
  rowState,
  checked,
  offboardResult,
  onSetState,
  onSetChecked,
  onOffboardResult,
  onError,
}: UserRowProps) {
  const { mutate: offboard } = useMutation({
    ...getOffboardUserMutation(),
    onMutate: () => {
      onSetState('offboarding');
    },
    onSuccess: data => {
      onOffboardResult({ orgs_removed: data.orgs_removed });
      onSetState('offboarded');
    },
    onError: () => {
      onError();
    },
  });

  const handleOffboardClick = () => {
    onSetState('confirming');
    onSetChecked(false);
  };

  const handleConfirm = () => {
    offboard(user.id);
  };

  const lastLogin = user.last_login_at
    ? new Date(user.last_login_at).toLocaleDateString()
    : 'Never';

  const isOffboarded = rowState === 'offboarded';

  return (
    <div
      className={`user-management__row${
        isOffboarded ? ' user-management__row--offboarded' : ''
      }`}
    >
      <div className="user-management__row-info">
        <div className="user-management__name">{user.name ?? '(no name)'}</div>
        <div className="user-management__email">{user.email}</div>
        <div className="user-management__last-login">
          Last login: {lastLogin}
        </div>

        {user.orgs && user.orgs.length > 0 && (
          <div className="user-management__orgs">
            {user.orgs.map(org => (
              <Text
                key={org.id}
                as="span"
                variant="paragraph_small"
                className="user-management__org-badge"
              >
                {org.name}
              </Text>
            ))}
          </div>
        )}

        {isOffboarded && offboardResult && (
          <>
            <Text
              as="span"
              variant="paragraph_small"
              className="user-management__offboarded-badge"
            >
              Offboarded
            </Text>
            <div className="user-management__removed-orgs">
              {offboardResult.orgs_removed.length > 0
                ? `Removed from: ${offboardResult.orgs_removed
                    .map(o => o.name)
                    .join(', ')}`
                : 'Removed from no organizations.'}
            </div>
          </>
        )}
      </div>

      <div className="user-management__row-actions">
        {rowState === 'idle' && (
          <Button variant="error" size="small" onClick={handleOffboardClick}>
            Offboard
          </Button>
        )}

        {rowState === 'confirming' && (
          <div className="user-management__confirm">
            <Row
              gutter="xs"
              align="center"
              className="user-management__confirm-check"
            >
              <Checkbox
                checked={checked}
                onCheckedChange={val => onSetChecked(val === true)}
              />
              <Text variant="paragraph_small">
                I confirm I want to offboard this user.
              </Text>
            </Row>
            <Row gutter="sm">
              <Button
                variant="secondary"
                size="small"
                onClick={() => onSetState('idle')}
              >
                Cancel
              </Button>
              <Button
                variant="error"
                size="small"
                disabled={!checked}
                onClick={handleConfirm}
              >
                Confirm Offboard
              </Button>
            </Row>
          </div>
        )}

        {rowState === 'offboarding' && (
          <Button variant="error" size="small" disabled>
            Offboarding...
          </Button>
        )}
      </div>
    </div>
  );
}
